import {
  llamaLendApiBaseUrl,
  llamaLendChainData,
  llamaLendConfigFor,
  type LlamaLendMarketConfig,
} from '@1delta/data-sdk'
import {
  Erc20Abi,
  LlamaLendAmmAbi,
  LlamaLendControllerAbi,
  LlamaLendControllerV1Abi,
  LlamaLendControllerV2Abi,
  LlamaLendVaultAbi,
} from '@1delta/abis'
import { multicallRetryUniversal } from '@1delta/providers'
import { bandLtvCurve } from '@1delta/calldata-sdk'
import type {
  CurveLendingVaultApiRow,
  LlamaLendMarketRaw,
  LlamaLendMarketsRaw,
} from './types'

/**
 * Hybrid public-data fetch for Curve LlamaLend — the Curve API first, a flat
 * on-chain multicall as fallback.
 *
 * `GET /v1/getLendingVaults/all` returns EVERY market on EVERY chain and in
 * BOTH generations in a single call, with rates, balances and token metadata
 * already in human units. That makes it the obvious primary; the fallback
 * exists because a lender that goes dark when a third-party API blips is not
 * acceptable.
 *
 * The market ROSTER always comes from lender-metadata
 * (`llamaLendConfig`/`llamaLendMarkets`), never from the API — the API is a
 * value source, so a compromised response cannot inject a market. Rows the API
 * returns that we do not carry are ignored.
 *
 * BAND MATH is not fetched at all any more: the LTV curve is derived from the
 * market's own geometry (see `buildBandLtv`). What still needs the chain is the
 * AMM oracle price, the band state, the v2 borrow cap, `maxDeposit` and the
 * vault's price per share — a fixed six reads per market.
 */

/** Curve's own UI default; used when a market row omits `defaultBands`. */
const DEFAULT_BANDS = 10

const LLAMALEND_READ_ABI = [
  ...LlamaLendControllerAbi,
  ...LlamaLendControllerV1Abi,
  ...LlamaLendControllerV2Abi,
  ...LlamaLendVaultAbi,
  ...LlamaLendAmmAbi,
  ...Erc20Abi,
] as any

const ZERO = '0x0000000000000000000000000000000000000000'
const WAD = 10n ** 18n

/** Fixed per-market read width in `fetchChainExtras` — keep in sync with the call list. */
const CALLS_PER_MARKET = 6

const fetchJson = async (url: string, timeoutMs = 8000): Promise<any> => {
  const res = await fetch(url, {
    headers: { accept: 'application/json' },
    signal: AbortSignal.timeout(timeoutMs),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

const num = (v: any): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

const toBig = (v: any): bigint | null => {
  if (typeof v === 'bigint') return v
  if (typeof v === 'number' && Number.isFinite(v)) return BigInt(Math.trunc(v))
  return null
}

const human = (v: bigint | null, decimals: number): number | null =>
  v === null ? null : Number(v) / 10 ** decimals

const bandsFor = (m: LlamaLendMarketConfig): number =>
  m.defaultBands && m.defaultBands >= 4 && m.defaultBands <= 50
    ? m.defaultBands
    : DEFAULT_BANDS

/** `MIN_TICKS` / `MAX_TICKS` on the Controller — identical on v1 and v2. */
const MIN_BANDS = 4
const MAX_BANDS = 50

/**
 * Every band count a borrower can actually choose.
 *
 * `bandLtv` used to be sampled at four points because each one cost an on-chain
 * `max_borrowable` read. It is now derived ANALYTICALLY from the market's
 * geometry (`ammA`, `loanDiscount`), so the full domain costs nothing but ~47
 * floats of payload — and the sampled version was actively harmful downstream:
 * a UI asked to show the LTV for the user's chosen `N` got a hit only when that
 * `N` happened to be 4/10/20/50, and blanks everywhere else.
 *
 * Consumers can therefore treat `bandLtv[N]` as total over `[4, 50]`.
 */
const LTV_CURVE_BANDS: number[] = Array.from(
  { length: MAX_BANDS - MIN_BANDS + 1 },
  (_, i) => MIN_BANDS + i,
)

/**
 * The band-LTV curve, computed ANALYTICALLY from the market's own geometry.
 *
 * This used to sample `Controller.max_borrowable(1 unit, N)` on-chain. That was
 * wrong three ways, and the third is what forced the change:
 *
 *  1. it returns 0 when the vault has nothing lendable, so an idle market
 *     published no LTV even though its parameter is perfectly well defined;
 *  2. it needs a live oracle price to divide by, so a momentarily stale oracle
 *     dropped the market too;
 *  3. **the arity is not stable across v1 markets.** Vyper compiles each
 *     default-arity to its own selector, and only the newer v1 blueprint has
 *     `max_borrowable(collateral, N, current_debt, user)` (`0x51abf14a`). On
 *     chain 1, SIXTEEN of 27 markets lack it — including WETH, wstETH, WBTC,
 *     sUSDe and tBTC — so the call REVERTED and those markets served no LTV at
 *     all. This was mis-diagnosed as "idle vault" for a while; the deepest
 *     markets on the chain were affected.
 *
 * `k_effective` is the same quantity the protocol derives internally, from
 * `loan_discount`, the AMM's `A` and `N` — no liquidity, no oracle, no
 * selector to guess. Validated against the chain at 0.9699 vs 0.9695 measured
 * (N=10), and it reproduces Curve's own advertised 50x at N=4.
 *
 * The residual ~0.04 % is the documented `max_p_base` grid approximation: the
 * Controller places a loan on a discrete price grid, so the realised number
 * lands within one band step. Fine for display and for a leverage cap; a
 * max-borrow SIZING call must still go to the chain.
 */
const buildBandLtv = (
  market: LlamaLendMarketConfig,
): { [bands: string]: number } | null => {
  if (!market.ammA || !market.loanDiscount) return null
  try {
    const curve = bandLtvCurve({
      ammA: BigInt(market.ammA),
      loanDiscount: BigInt(market.loanDiscount),
      // Reference size only — it feeds the DEAD_SHARES cushion, which is
      // negligible at any realistic position size and converges as it grows.
      collateral: 10n ** BigInt(market.collateralDecimals + 3),
      collateralDecimals: market.collateralDecimals,
      // The FULL domain, not the multicall grid — see `LTV_CURVE_BANDS`.
      bandCounts: LTV_CURVE_BANDS,
    })
    return Object.keys(curve).length > 0 ? curve : null
  } catch {
    // Bad geometry in metadata (A <= 1) — publish nothing rather than a wrong
    // number. `null` reads as "unknown", never as "worthless".
    return null
  }
}

/**
 * Chain-only reads that the API cannot answer: the LTV curve, the AMM oracle
 * price, the v2 borrow cap, `maxDeposit` and the active band.
 *
 * Returns a per-controller map; missing entries mean the read failed and the
 * caller degrades those fields to `null`.
 */
async function fetchChainExtras(
  chainId: string,
  markets: LlamaLendMarketConfig[],
): Promise<{
  [controllerLower: string]: {
    collateralPrice: number | null
    bandLtv: { [bands: string]: number } | null
    collateralFactor: number | null
    borrowCap: number | null
    maxDeposit: number | null
    nLoans: number | null
    activeBand: number | null
    /** Assets per 1e18 shares. `DEAD_SHARES` puts this ~1e15, not ~1e18. */
    pricePerShare: number | null
  }
}> {
  const perMarketCalls = markets.map((m) => {
    const calls = [
      { address: m.amm, name: 'price_oracle', params: [] },
      { address: m.amm, name: 'active_band', params: [] },
      { address: m.controller, name: 'n_loans', params: [] },
      { address: m.vault, name: 'maxDeposit', params: [ZERO] },
      // v1 controllers have no `borrow_cap`; allowFailure turns that into a
      // null rather than sinking the whole batch.
      { address: m.controller, name: 'borrow_cap', params: [] },
      /**
       * Price per share, read ONCE PER MARKET rather than per user.
       *
       * Vault shares run ~1000x the asset scale (`DEAD_SHARES`), so a share
       * balance is never an amount — every consumer needs this multiplier to
       * turn a lender's holding into assets. Reading it here keeps the
       * per-user call to two plain `balanceOf`s.
       */
      { address: m.vault, name: 'convertToAssets', params: [WAD] },
    ]
    return { market: m, calls }
  })

  const flat = perMarketCalls.flatMap((x) => x.calls)
  if (flat.length === 0) return {}

  let results: any[]
  try {
    results = (await multicallRetryUniversal({
      chain: chainId,
      calls: flat,
      abi: LLAMALEND_READ_ABI,
      allowFailure: true,
    })) as any[]
  } catch (e) {
    // Everything derived on-chain dies with this batch — the LTV curve, the
    // oracle price, the band state, the borrow cap — for EVERY market on the
    // chain at once. Swallowing it silently made a total read failure
    // indistinguishable from a stale build: production served
    // `collateralFactor: 0` on all 20 LlamaLend markets with nothing in the
    // logs to say why. Loud enough to grep for, still non-fatal: the caller
    // degrades the affected fields to null.
    console.error(
      `[llamalend] chain-extras multicall failed on chain ${chainId} for ${markets.length} market(s) — LTV/price/band state unavailable this cycle:`,
      (e as any)?.message ?? e,
    )
    return {}
  }

  const out: {
    [k: string]: {
      collateralPrice: number | null
      bandLtv: { [bands: string]: number } | null
      collateralFactor: number | null
      borrowCap: number | null
      maxDeposit: number | null
      nLoans: number | null
      activeBand: number | null
      pricePerShare: number | null
    }
  } = {}
  let cursor = 0
  for (const { market } of perMarketCalls) {
    const priceRaw = toBig(results[cursor])
    const activeBandRaw = toBig(results[cursor + 1])
    const nLoansRaw = toBig(results[cursor + 2])
    const maxDepositRaw = toBig(results[cursor + 3])
    const borrowCapRaw = toBig(results[cursor + 4])
    const pricePerShareRaw = toBig(results[cursor + 5])
    cursor += CALLS_PER_MARKET

    // The AMM oracle is always WAD-scaled regardless of token decimals.
    const collateralPrice = priceRaw === null ? null : Number(priceRaw) / 1e18
    const bandLtv = buildBandLtv(market)
    const defaultN = String(bandsFor(market))

    out[market.controller.toLowerCase()] = {
      collateralPrice,
      bandLtv,
      collateralFactor: bandLtv?.[defaultN] ?? null,
      borrowCap: human(borrowCapRaw, market.borrowedDecimals),
      maxDeposit: human(maxDepositRaw, market.borrowedDecimals),
      nLoans: nLoansRaw === null ? null : Number(nLoansRaw),
      activeBand: activeBandRaw === null ? null : Number(activeBandRaw),
      // Assets-per-WAD-shares. Kept as a ratio (not scaled to the borrowed
      // token's decimals) so a consumer multiplies a raw share balance by it
      // and divides by 1e18 exactly once.
      pricePerShare:
        pricePerShareRaw === null ? null : Number(pricePerShareRaw) / 1e18,
    }
  }
  return out
}

/**
 * `borrowingEnabled` is not a single flag on either generation.
 *
 * v2 gates on `borrow_cap` (which starts at 0 and therefore DISABLES borrowing
 * until governance raises it) as well as on there being liquidity to lend.
 * v1 has no cap, so liquidity alone decides.
 */
const isBorrowingEnabled = (
  version: 1 | 2,
  availableToBorrow: number | null,
  borrowCap: number | null,
  totalDebt: number | null,
): boolean => {
  const liquid = (availableToBorrow ?? 0) > 0
  if (version === 1) return liquid
  if (borrowCap === null) return liquid
  if (borrowCap <= 0) return false
  return liquid && (totalDebt ?? 0) < borrowCap
}

export async function fetchLlamaLendMarkets(
  lender: string,
  chainId: string,
): Promise<LlamaLendMarketsRaw> {
  const config = llamaLendConfigFor(lender, chainId)
  const chainData = llamaLendChainData(lender, chainId)
  const markets = chainData?.markets ?? []
  const empty: LlamaLendMarketsRaw = {
    lender,
    config,
    chainData,
    markets: [],
    source: 'none',
  }
  if (!config || markets.length === 0) return empty

  // Band math is chain-only and independent of which value source wins, so
  // kick it off in parallel with the API call rather than after it.
  const extrasPromise = fetchChainExtras(chainId, markets)

  // --- primary: the Curve API ---
  try {
    const base = llamaLendApiBaseUrl(lender, chainId)
    const api = await fetchJson(`${base}/v1/getLendingVaults/all`)
    const byController: { [addr: string]: CurveLendingVaultApiRow } = {}
    for (const row of api?.data?.lendingVaultData ?? []) {
      const c = row?.controllerAddress
      if (c) byController[String(c).toLowerCase()] = row
    }
    if (Object.keys(byController).length > 0) {
      const extras = await extrasPromise
      const rows: LlamaLendMarketRaw[] = markets.map((market) => {
        const r = byController[market.controller.toLowerCase()]
        const ex = extras[market.controller.toLowerCase()]
        const totalDebt = num(r?.borrowed?.total)
        const availableToBorrow = num(r?.availableToBorrow?.total)
        return {
          market,
          totalDebt,
          totalSupplied: num(r?.totalSupplied?.total),
          availableToBorrow,
          // `borrowApr`/`lendApr` are the NOMINAL rates and match
          // `Vault.borrow_apr()`/`lend_apr()`. The sibling `borrowApy` /
          // `lendApy` fields are compounded — mixing them in would make this
          // lender look systematically more expensive than its peers.
          borrowApr: num(r?.rates?.borrowApr),
          lendApr: num(r?.rates?.lendApr),
          collateralPrice: ex?.collateralPrice ?? null,
          collateralPriceUsd: num(r?.assets?.collateral?.usdPrice),
          borrowedPriceUsd: num(r?.assets?.borrowed?.usdPrice),
          collateralFactor: ex?.collateralFactor ?? null,
          bandLtv: ex?.bandLtv ?? null,
          borrowCap: ex?.borrowCap ?? null,
          borrowingEnabled: isBorrowingEnabled(
            market.version,
            availableToBorrow,
            ex?.borrowCap ?? null,
            totalDebt,
          ),
          maxDeposit: ex?.maxDeposit ?? null,
          nLoans: ex?.nLoans ?? null,
          activeBand: ex?.activeBand ?? null,
          pricePerShare: ex?.pricePerShare ?? null,
        }
      })
      return { lender, config, chainData, markets: rows, source: 'api' }
    }
  } catch {}

  // --- fallback: flat on-chain multicall ---
  // 5 reads per market, in the order built below. Keep in sync with the slicer.
  const READS_PER_MARKET = 5
  const calls = markets.flatMap((m) => [
    { address: m.controller, name: 'total_debt', params: [] },
    { address: m.vault, name: 'totalAssets', params: [] },
    { address: m.vault, name: 'borrow_apr', params: [] },
    { address: m.vault, name: 'lend_apr', params: [] },
    // v2 tracks its own lent-out accounting; v1 has no such getter, so the
    // controller's token balance IS the borrowable liquidity there.
    m.version === 2
      ? { address: m.controller, name: 'available_balance', params: [] }
      : { address: m.borrowedToken, name: 'balanceOf', params: [m.controller] },
  ])

  try {
    const results = (await multicallRetryUniversal({
      chain: chainId,
      calls,
      abi: LLAMALEND_READ_ABI,
      allowFailure: true,
    })) as any[]
    const extras = await extrasPromise
    const rows: LlamaLendMarketRaw[] = markets.map((market, i) => {
      const b = i * READS_PER_MARKET
      const ex = extras[market.controller.toLowerCase()]
      const totalDebt = human(toBig(results[b]), market.borrowedDecimals)
      const availableToBorrow = human(
        toBig(results[b + 4]),
        market.borrowedDecimals,
      )
      const borrowAprRaw = toBig(results[b + 2])
      const lendAprRaw = toBig(results[b + 3])
      const collateralPrice = ex?.collateralPrice ?? null
      return {
        market,
        totalDebt,
        totalSupplied: human(toBig(results[b + 1]), market.borrowedDecimals),
        availableToBorrow,
        // Both getters are already annualised nominal rates, WAD-scaled.
        borrowApr: borrowAprRaw === null ? null : Number(borrowAprRaw) / 1e18,
        lendApr: lendAprRaw === null ? null : Number(lendAprRaw) / 1e18,
        collateralPrice,
        // No USD leg on-chain — the oracle prices collateral in the borrowed
        // token only. The converter falls back to the global price oracle.
        collateralPriceUsd: null,
        borrowedPriceUsd: null,
        collateralFactor: ex?.collateralFactor ?? null,
        bandLtv: ex?.bandLtv ?? null,
        borrowCap: ex?.borrowCap ?? null,
        borrowingEnabled: isBorrowingEnabled(
          market.version,
          availableToBorrow,
          ex?.borrowCap ?? null,
          totalDebt,
        ),
        maxDeposit: ex?.maxDeposit ?? null,
        nLoans: ex?.nLoans ?? null,
        activeBand: ex?.activeBand ?? null,
        pricePerShare: ex?.pricePerShare ?? null,
      }
    })
    return { lender, config, chainData, markets: rows, source: 'chain' }
  } catch {
    return empty
  }
}
